import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";

/**
 * Admin console — /admin/loyalty
 *
 * Single-form config for the entire Loyalty Coins + Refer&Earn +
 * Free-Shipping perk module. Reads and writes the tenant's
 * LoyaltyRules doc via /api/loyalty/admin/rules.
 *
 * Kept as one flat form on purpose — a merchant configures this
 * once and rarely touches it again. Tab-splitting would just add
 * clicks without clarity.
 */
export default function AdminLoyalty() {
  const [rules, setRules] = useState(null);
  const [saving, setSaving] = useState(false);
  const [backfilling, setBackfilling] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/loyalty/admin/rules");
        setRules(data);
      } catch {
        toast.error("Failed to load loyalty rules");
      }
    })();
  }, []);

  if (!rules) return <div className="p-6">Loading…</div>;

  const set = (k, v) => setRules((r) => ({ ...r, [k]: v }));
  const setNested = (k, sub, v) =>
    setRules((r) => ({ ...r, [k]: { ...(r[k] || {}), [sub]: v } }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/loyalty/admin/rules", rules);
      setRules(data);
      toast.success("Loyalty rules saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
    setSaving(false);
  };

  const backfill = async () => {
    setBackfilling(true);
    try {
      const { data } = await api.post("/loyalty/admin/backfill-scratch-cards?days=30");
      if (data.minted > 0) {
        toast.success(`Minted ${data.minted} card${data.minted === 1 ? "" : "s"} · scanned ${data.scanned}`);
      } else {
        toast.info(`All ${data.scanned} recent deliveries already have cards`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Backfill failed");
    }
    setBackfilling(false);
  };

  return (
    <div className="p-6 max-w-3xl" data-testid="admin-loyalty-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-medium">Loyalty & Rewards</h1>
          <p className="text-sm text-neutral-500 mt-1">
            Configure how customers earn and redeem {rules.labels?.coin_plural || "coins"}
          </p>
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={!!rules.enabled}
            onChange={(e) => set("enabled", e.target.checked)}
            data-testid="loyalty-enabled"
          />
          Enable module
        </label>
      </div>

      {/* Labels */}
      <Card title="Program Name">
        <Row>
          <Field label="Program label">
            <input
              className="kb-input"
              value={rules.labels?.program || ""}
              onChange={(e) => setNested("labels", "program", e.target.value)}
              placeholder="Loyalty"
              data-testid="label-program"
            />
          </Field>
          <Field label={`Coin — singular`}>
            <input
              className="kb-input"
              value={rules.labels?.coin_singular || ""}
              onChange={(e) => setNested("labels", "coin_singular", e.target.value)}
              placeholder="Coin"
              data-testid="label-coin-singular"
            />
          </Field>
          <Field label={`Coin — plural`}>
            <input
              className="kb-input"
              value={rules.labels?.coin_plural || ""}
              onChange={(e) => setNested("labels", "coin_plural", e.target.value)}
              placeholder="Coins"
              data-testid="label-coin-plural"
            />
          </Field>
        </Row>
      </Card>

      {/* Redemption */}
      <Card title="Redemption at Checkout">
        <label className="flex items-center gap-2 text-sm mb-3" data-testid="redemption-enabled-row">
          <input
            type="checkbox"
            checked={rules.redemption_enabled !== false}
            onChange={(e) => set("redemption_enabled", e.target.checked)}
            data-testid="redemption-enabled"
          />
          <span>
            Allow customers to <b>spend {rules.labels?.coin_plural || "coins"}</b> at checkout
          </span>
        </label>
        <p className="text-xs text-neutral-500 mb-3">
          When off, the &ldquo;apply coins&rdquo; widget is hidden on the checkout page and any coins the client sends are silently zeroed server-side. Earning still works.
        </p>
        <Row>
          <Field label="Coin → INR ratio (1 coin = ₹?)">
            <input
              type="number"
              step="0.01"
              className="kb-input"
              value={rules.coin_to_inr_ratio ?? 1}
              onChange={(e) => set("coin_to_inr_ratio", parseFloat(e.target.value))}
              data-testid="ratio"
            />
          </Field>
          <Field label="Min coins per order">
            <input
              type="number"
              className="kb-input"
              value={rules.min_coins_per_order ?? 10}
              onChange={(e) => set("min_coins_per_order", parseInt(e.target.value, 10))}
              data-testid="min-coins"
            />
          </Field>
          <Field label="Max coins per order (absolute)">
            <input
              type="number"
              className="kb-input"
              value={rules.max_coins_per_order ?? 500}
              onChange={(e) => set("max_coins_per_order", parseInt(e.target.value, 10))}
              data-testid="max-coins"
            />
          </Field>
          <Field label="Max redemption (% of order)">
            <input
              type="number"
              className="kb-input"
              value={rules.max_coins_per_order_pct ?? 30}
              onChange={(e) => set("max_coins_per_order_pct", parseInt(e.target.value, 10))}
              data-testid="max-pct"
            />
          </Field>
        </Row>
      </Card>

      {/* Cashback scratch cards */}
      <Card title="Cashback Scratch Cards (on delivery)">
        <label className="flex items-center gap-2 text-sm mb-3" data-testid="cashback-enabled-row">
          <input
            type="checkbox"
            checked={!!rules.cashback?.enabled}
            onChange={(e) => setNested("cashback", "enabled", e.target.checked)}
            data-testid="cashback-enabled"
          />
          <span>
            Issue a scratch card when an order is <b>delivered</b>
          </span>
        </label>
        <p className="text-xs text-neutral-500 mb-3">
          When on, the immediate &ldquo;Purchase reward&rdquo; credit above is <b>skipped</b> and those {rules.labels?.coin_plural || "coins"} are deferred to a scratch card. The customer taps the card in <code>/account/rewards</code> to reveal a random reward between the min and max below.
        </p>
        <Row>
          <Field label="Min coins per card">
            <input
              type="number"
              min="1"
              className="kb-input"
              value={rules.cashback?.min_coins ?? 5}
              onChange={(e) => setNested("cashback", "min_coins", parseInt(e.target.value, 10) || 0)}
              data-testid="cashback-min"
            />
          </Field>
          <Field label="Max coins per card">
            <input
              type="number"
              min="1"
              className="kb-input"
              value={rules.cashback?.max_coins ?? 50}
              onChange={(e) => setNested("cashback", "max_coins", parseInt(e.target.value, 10) || 0)}
              data-testid="cashback-max"
            />
          </Field>
          <Field label="Only orders ≥ ₹">
            <input
              type="number"
              className="kb-input"
              value={rules.cashback?.min_order_inr ?? 0}
              onChange={(e) => setNested("cashback", "min_order_inr", parseFloat(e.target.value) || 0)}
              data-testid="cashback-min-order"
            />
          </Field>
          <Field label="Card expires after (days)">
            <input
              type="number"
              min="1"
              className="kb-input"
              value={rules.cashback?.expiry_days ?? 30}
              onChange={(e) => setNested("cashback", "expiry_days", parseInt(e.target.value, 10) || 30)}
              data-testid="cashback-expiry"
            />
          </Field>
        </Row>
        <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-md flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="text-xs text-amber-900 flex-1">
            <b>Missed some deliveries?</b> If you turned Cashback on <i>after</i> some orders were already delivered, backfill mints cards for delivered orders in the last 30 days that don&apos;t have one yet.
          </div>
          <button
            type="button"
            onClick={backfill}
            disabled={!rules.cashback?.enabled || backfilling}
            className="text-xs px-3 py-2 rounded-full bg-amber-600 text-white disabled:opacity-40 whitespace-nowrap"
            data-testid="cashback-backfill-btn"
          >
            {backfilling ? "Working…" : "Backfill last 30 days"}
          </button>
        </div>
      </Card>

      {/* Earning rules — each with its own enable toggle */}
      <Card title="Earning Rules">
        <RuleRow
          testid="signup-bonus"
          label="Signup bonus"
          rule={rules.signup_bonus}
          onChange={(v) => set("signup_bonus", v)}
        />
        <div className="border-b border-neutral-100 my-3" />
        <RuleRow
          testid="purchase"
          label="Purchase — coins per ₹100 spent"
          rule={rules.purchase}
          coinsField="coins_per_100_inr"
          onChange={(v) => set("purchase", v)}
        />
        <div className="border-b border-neutral-100 my-3" />
        <RuleRow
          testid="review"
          label="Product review reward"
          rule={rules.review}
          onChange={(v) => set("review", v)}
          extra={
            <Field label="Min rating">
              <input
                type="number"
                min="1"
                max="5"
                className="kb-input w-20"
                value={rules.review?.min_rating ?? 4}
                onChange={(e) => set("review", { ...rules.review, min_rating: parseInt(e.target.value, 10) })}
                data-testid="review-min-rating"
              />
            </Field>
          }
        />
        <div className="border-b border-neutral-100 my-3" />
        <RuleRow
          testid="daily-checkin"
          label="Daily check-in"
          rule={rules.daily_checkin}
          onChange={(v) => set("daily_checkin", v)}
          extra={
            <Field label="7-day streak bonus">
              <input
                type="number"
                className="kb-input w-24"
                value={rules.daily_checkin?.streak_bonus_7d ?? 50}
                onChange={(e) => set("daily_checkin", { ...rules.daily_checkin, streak_bonus_7d: parseInt(e.target.value, 10) })}
                data-testid="checkin-streak"
              />
            </Field>
          }
        />
      </Card>

      {/* Referral */}
      <Card title="Refer & Earn">
        <p className="text-xs text-neutral-500 mb-3">
          Coins awarded to BOTH the referrer and the invitee when the invitee
          places their first paid order.
        </p>
        <RuleRow
          testid="referral-referrer"
          label="Referrer reward"
          rule={rules.referral_referrer}
          onChange={(v) => set("referral_referrer", v)}
        />
        <div className="border-b border-neutral-100 my-3" />
        <RuleRow
          testid="referral-invitee"
          label="Invitee welcome bonus"
          rule={rules.referral_invitee}
          onChange={(v) => set("referral_invitee", v)}
        />
      </Card>

      {/* Free shipping */}
      <Card title="Free Shipping Threshold">
        <p className="text-xs text-neutral-500 mb-3">
          When on, orders whose (subtotal − discount − coin redemption) meet
          the threshold ship free. When off, the legacy ₹1999 threshold is used.
        </p>
        <Row>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={!!rules.free_shipping_enabled}
              onChange={(e) => set("free_shipping_enabled", e.target.checked)}
              data-testid="fs-enabled"
            />
            Enable free-shipping threshold
          </label>
          <Field label="Free shipping when order ≥ ₹">
            <input
              type="number"
              className="kb-input"
              value={rules.free_shipping_threshold_inr ?? 499}
              onChange={(e) => set("free_shipping_threshold_inr", parseFloat(e.target.value))}
              data-testid="fs-threshold"
            />
          </Field>
        </Row>
      </Card>

      <div className="mt-6 flex gap-2">
        <button
          className="px-6 py-2.5 bg-neutral-900 text-white rounded-full text-sm disabled:opacity-50"
          disabled={saving}
          onClick={save}
          data-testid="save-loyalty"
        >
          {saving ? "Saving…" : "Save changes"}
        </button>
      </div>
    </div>
  );
}

function Card({ title, children }) {
  return (
    <div className="border border-neutral-200 rounded-lg p-5 mb-4">
      <div className="overline mb-3">{title}</div>
      {children}
    </div>
  );
}
function Row({ children }) {
  return <div className="grid grid-cols-1 md:grid-cols-3 gap-3">{children}</div>;
}
function Field({ label, children }) {
  return (
    <label className="text-xs text-neutral-600 flex flex-col gap-1">
      {label}
      {children}
    </label>
  );
}
function RuleRow({ testid, label, rule, onChange, extra, coinsField = "coins" }) {
  const r = rule || {};
  return (
    <div className="flex flex-wrap items-end gap-3">
      <label className="flex items-center gap-2 text-sm flex-1 min-w-[220px]">
        <input
          type="checkbox"
          checked={!!r.enabled}
          onChange={(e) => onChange({ ...r, enabled: e.target.checked })}
          data-testid={`${testid}-enabled`}
        />
        {label}
      </label>
      <Field label={`Coins (${coinsField})`}>
        <input
          type="number"
          className="kb-input w-28"
          value={r[coinsField] ?? 0}
          onChange={(e) => onChange({ ...r, [coinsField]: parseInt(e.target.value, 10) || 0 })}
          data-testid={`${testid}-coins`}
        />
      </Field>
      {extra}
    </div>
  );
}
